import { makeFormData } from "utils/makeFormData";
import { emptyApi } from "./emptyApi";
import { File } from "buffer";

export type RegistrationStatus =
  | "PENDING_DETAILS"
  | "IN_REVIEW"
  | "PROCESSING"
  | "REGISTERED"
  | "NOT_STARTED"
  | "VALIDATING"
  | "FILING_IN_PROGRESS"
  | "FILING_INFO_MISSING";

export type PreFilledDueDateByStateIdResponse = {
  filing_due_date: string;
  payment_schedule: "MONTHLY" | "QUARTERLY" | "SEMI_ANNUALLY" | "ANNUALLY";
};

export type Registration = {
  state_registration_id: string;
  state: string;
  tax_detail_id?: string;
  request_date: string;
  status: RegistrationStatus;
  state_id: string;
  launched_task_id?: string | null;
  application?:
    | string
    | {
        file_name?: string;
        file_id?: string;
      };
  registration_license:
    | string
    | {
        file_name?: string;
        file_id?: string;
      };
};

type payment_schedule = "Monthly" | "Quarterly" | "Semi-annually" | "Annually";

export type AddRegistrationDetailsPayload = {
  sales_tax_id: string;
  payment_schedule: payment_schedule;
  filing_start_date: string;
  registration_license:
    | string
    | {
        file_name?: string;
        file_id?: string;
      };
  application?: string;
};

export type AddExistingRegistrationPayload = {
  state_id: string;
  tax_detail_id: string;
  sales_tax_id: string;
  payment_schedule: payment_schedule;
  filing_start_date: string;
  registration_license:
    | File
    | {
        file_name?: string;
        file_id?: string;
      };
  application: {
    file_name?: string;
    file_id?: string;
  };
};

export type SalesTaxRegistrationUpdatePayload = {
  filing_start_date: string | null;
  tax_detail_id?: string;
};

export type PreSalesTaxRegistrationPayload =
  SalesTaxRegistrationUpdatePayload & {
    state_id: string;
  };

export type PostSalesRegistrationPayload = {
  sales_tax_id?: string;
  payment_schedule: payment_schedule;
  filing_start_date: string;
  registration_license_id: string;
  tax_detail_id?: string;
  status?: "REGISTERED";
  application?:
    | File
    | {
        file_name?: string;
        file_id?: string;
      };
};

export type CompleteFilingDetailsPayload = {
  payment_schedule: payment_schedule;
  filing_start_date: string;
  sales_tax_id?: string;
};

export type AlreadyRegisteredPayload = {
  tax_detail_id: string;
  registration_license: globalThis.File;
  status: "REGISTERED";
};
export type RegistrationDetailsResponse = {
  uuid?: string;
  state_registration_id: string;
  request_date: string;
  status: RegistrationStatus;
  state: string;
  state_id: string;
  sales_tax_id: string;
  payment_schedule:
    | "ANNUALLY"
    | "MONTHLY"
    | "QUARTERLY"
    | "SEMI_ANNUALLY"
    | "NONE";
  filing_start_date: string;
  registration_license?: {
    file_name: string;
    file_id: string;
  };
  application?: {
    file_name: string;
    file_id: string;
  };
  tax_detail_id?: string;
  filing_due_date?: string;
  service_team?: {
    uuid: string;
    name: string;
  };
  preparer?: {
    profile_id?: string;
    name?: string;
  };
  assigned_on?: string;
  login_email?: string;
};

type ExemptedState = {
  uuid: string;
  state: string;
  postal_code: string;
};
export const salesTaxRegistration = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getSalesTaxRegistrations: build.query<Registration[], { entityId: string }>(
      {
        query: ({ entityId }) => ({
          url: `/api/inkle/salestax/entity/${entityId}/registrations/`,
        }),
        providesTags: ["SALES_TAX_REGISTRATIONS"],
      }
    ),
    updatePreSalesTaxRegistrations: build.mutation<
      Registration[],
      {
        entityId: string;
        state_registration_id: string;
        payload: SalesTaxRegistrationUpdatePayload;
      }
    >({
      query: ({ entityId, payload, state_registration_id }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/registrations/pre/${state_registration_id}/`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["SALES_TAX_REGISTRATIONS"],
    }),

    createPreSalesRegistraions: build.mutation<
      Registration,
      {
        entityId: string;
        payload: PreSalesTaxRegistrationPayload;
      }
    >({
      query: ({ entityId, payload }) => {
        return {
          url: `/api/inkle/salestax/entity/${entityId}/registrations/pre/`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ["SALES_TAX_REGISTRATIONS"],
    }),

    updatePostSalesTaxRegistrations: build.mutation<
      Registration[],
      {
        entityId: string;
        state_registration_id: string;
        payload: SalesTaxRegistrationUpdatePayload;
      }
    >({
      query: ({ entityId, payload, state_registration_id }) => {
        const formData = makeFormData(payload);
        return {
          url: `/api/inkle/salestax/entity/${entityId}/registrations/post/${state_registration_id}/`,
          method: "PUT",
          body: formData,
        };
      },
      invalidatesTags: ["SALES_TAX_REGISTRATIONS"],
    }),

    createPostSalesRegistrations: build.mutation<
      Registration,
      {
        entityId: string;
        payload: PostSalesRegistrationPayload;
      }
    >({
      query: ({ entityId, payload }) => {
        const formData = makeFormData(payload);
        return {
          url: `/api/inkle/salestax/entity/${entityId}/registrations/post/`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result) =>
        result ? ["SALES_TAX_REGISTRATIONS", "FILINGS"] : [],
    }),
    submitAlreadyRegistered: build.mutation<
      Registration,
      {
        entityId: string;
        stateId: string;
        payload: AlreadyRegisteredPayload;
      }
    >({
      query: ({ entityId, stateId, payload }) => {
        const formData = makeFormData({ ...payload, state_id: stateId });
        return {
          url: `/api/inkle/salestax/entity/${entityId}/registrations/post/`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result) =>
        result ? ["SALES_TAX_REGISTRATIONS", "NEXUSTRACKER"] : [],
    }),
    getSalesTaxRegistrationDetails: build.query<
      RegistrationDetailsResponse,
      { entityId: string; state_registration_id: string }
    >({
      query: ({ entityId, state_registration_id }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/registrations/${state_registration_id}`,
      }),
    }),
    getPreFilledDueDateByStateId: build.query<
      PreFilledDueDateByStateIdResponse[],
      { entityId: string; stateId: string }
    >({
      query: ({ entityId, stateId }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/filings/state/${stateId}/due_dates/`,
      }),
    }),
    getNonExemptedState: build.query<ExemptedState[], { entityId: string }>({
      query: ({ entityId }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/nexus_tracker/states/`,
      }),
    }),
    completeFilingDetails: build.mutation<
      Registration,
      {
        entityId: string;
        registrationId: string;
        payload: CompleteFilingDetailsPayload;
      }
    >({
      query: ({ entityId, registrationId, payload }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/registrations/${registrationId}/complete-filing-details/`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["SALES_TAX_REGISTRATIONS", "FILINGS"],
    }),
  }),
});

export const {
  useGetSalesTaxRegistrationsQuery,
  useUpdatePreSalesTaxRegistrationsMutation,
  useCreatePreSalesRegistraionsMutation,
  useCreatePostSalesRegistrationsMutation,
  useGetSalesTaxRegistrationDetailsQuery,
  useUpdatePostSalesTaxRegistrationsMutation,
  useGetPreFilledDueDateByStateIdQuery,
  useGetNonExemptedStateQuery,
  useSubmitAlreadyRegisteredMutation,
  useCompleteFilingDetailsMutation,
} = salesTaxRegistration;
